import { createMemo, createResource, For, Show } from "solid-js"
import { DropdownMenu } from "@opencode-ai/ui/dropdown-menu"
import { Icon } from "@opencode-ai/ui/v2/icon"
import { useLanguage } from "@/context/language"
import { listOutputRevisions } from "@/datox/api"

export function DatoxRevisionSelect(props: { projectID: string; outputID: string; kind: "report" | "dashboard"; value: number; refreshKey?: string; disabled?: boolean; appearance?: "menu"; onChange: (value: number) => void }) {
  const language = useLanguage()
  const [revisions] = createResource(() => `${props.projectID}:${props.kind}:${props.outputID}:${props.refreshKey ?? ""}`,
    () => listOutputRevisions(props.projectID, props.outputID, props.kind))
  const options = createMemo(() => revisions.error ? [] : (revisions.latest ?? []).filter((item) => item.output_id === props.outputID).sort((a, b) => b.number - a.number))
  return <div class="flex min-w-0 max-w-full flex-wrap items-center gap-2 text-12-regular">
    <Show when={props.appearance !== "menu"} fallback={
      <DropdownMenu gutter={6} placement="bottom-start">
        <DropdownMenu.Trigger class="lesha-revision-trigger" disabled={props.disabled || revisions.loading}
          aria-label={language.t("datox.revisions.label")}>
          <Icon name="clock" /><span>{language.t("datox.revisions.label")}</span>
          <strong>{props.value ? language.t("datox.report.version", { version: props.value }) : language.t("datox.revisions.latest")}</strong><Icon name="chevron-down" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content class="lesha-revision-menu" aria-label={language.t("datox.revisions.label")}>
            <Show when={!revisions.loading} fallback={<p role="status">{language.t("datox.revisions.loading")}</p>}>
                <DropdownMenu.RadioGroup value={String(props.value)} onChange={(value) => {
                  if (props.disabled || revisions.loading) return
                  const number = Number(value)
                  if (number !== 0 && (revisions.error || !options().some((item) => item.number === number && item.status === "completed"))) return
                  props.onChange(number)
                }}>
                  <DropdownMenu.RadioItem value="0" closeOnSelect><span>{language.t("datox.revisions.latest")}</span><DropdownMenu.ItemIndicator><Icon name="check" /></DropdownMenu.ItemIndicator></DropdownMenu.RadioItem>
                  <DropdownMenu.Separator />
                  <Show when={revisions.error}><p role="alert">{language.t("datox.revisions.error")}</p></Show>
                  <For each={options()}>{(item) => <DropdownMenu.RadioItem value={String(item.number)} closeOnSelect disabled={item.status !== "completed"}>
                    <span>{language.t(item.status === "completed" ? "datox.revisions.option" : "datox.revisions.incomplete", { number: item.number, date: item.created_at.replace("T", " ").slice(0, 16) })}</span>
                    <DropdownMenu.ItemIndicator><Icon name="check" /></DropdownMenu.ItemIndicator>
                  </DropdownMenu.RadioItem>}</For>
                </DropdownMenu.RadioGroup>
            </Show>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu>
    }>
    <label class="flex min-w-0 max-w-full items-center gap-2"><span>{language.t("datox.revisions.label")}</span>
      <select data-testid="output-revision-picker" class="min-w-0 max-w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2 text-12-regular"
        aria-label={language.t("datox.revisions.label")} value={props.value || ""} disabled={props.disabled || revisions.loading}
        onChange={(event) => props.onChange(Number(event.currentTarget.value) || 0)}>
        <option value="">{language.t("datox.revisions.latest")}</option>
        <For each={options()}>{(item) => <option value={item.number} disabled={item.status !== "completed"}>
          {language.t(item.status === "completed" ? "datox.revisions.option" : "datox.revisions.incomplete", { number: item.number, date: item.created_at.replace("T", " ").slice(0, 16) })}
        </option>}</For>
      </select>
    </label>
    </Show>
    <Show when={revisions.error}><span role="status">{language.t("datox.revisions.error")}</span></Show>
    <Show when={props.value}><span class="text-v2-text-text-muted">{language.t("datox.revisions.readOnly")}</span></Show>
  </div>
}
